import get from 'lodash/get';
import isString from 'lodash/isString';
import isNil from 'lodash/isNil';
import isEqual from 'lodash/isEqual';
import { CodeInputValue } from './Survey';
import ExtensionCollection from './ExtensionCollection';
import surveyUrls from './surveyUrls';

export type SurveyValueSetPurpose = 'survey-list' | 'survey-score-labels';

export class Coding {
  _code: string;
  _display: string;
  _system: string;
  _numericValue: number | null;
  _description: string | null;
  _purpose: SurveyValueSetPurpose | null;

  static initFromFormValue(
    code: CodeInputValue,
    defaultSystemUrl: string,
    enableNumericValues: boolean,
    purpose?: SurveyValueSetPurpose,
  ) {
    return new Coding(
      code.code || code.display,
      code.display,
      defaultSystemUrl,
      enableNumericValues ? code.numericValue : null,
      code.description,
      purpose,
    );
  }

  constructor(
    code: string,
    display: string,
    system: string,
    numericValue: number | null,
    description?: string | null,
    purpose?: SurveyValueSetPurpose | null,
  ) {
    this._code = code;
    this._display = display;
    this._system = system;
    this._numericValue = isNil(numericValue) ? null : numericValue;
    this._description = isNil(description) ? null : description;
    this._purpose = isNil(purpose) ? null : purpose;
  }

  get code(): string {
    return this._code;
  }

  get display(): string {
    return this._display;
  }

  get isValid(): string {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return !!this._code && this._display;
  }

  get system(): string {
    return this._system;
  }

  get numericValue(): number | null {
    return this._numericValue;
  }

  get description(): string | null {
    return this._description;
  }

  get fhirCoding(): fhir.Coding {
    const returnValue: fhir.Coding = {
      display: this._display,
      code: this._code,
    };

    const hasNumericValue = !isNil(this._numericValue);
    const hasDescription = !isNil(this._description);

    if (hasNumericValue || hasDescription) {
      returnValue.extension = [];
    }

    if (hasNumericValue) {
      const url =
        this._purpose === 'survey-score-labels'
          ? surveyUrls.surveyScoreLabelNumericValue
          : surveyUrls.surveyListOptionNumericValue;

      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      returnValue.extension.push({
        url,
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        valueDecimal: this._numericValue,
      });
    }

    if (hasDescription) {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      returnValue.extension.push({
        url: surveyUrls.surveyScoreLabelMarkdownValue,
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        valueMarkdown: this._description,
      });
    }

    return returnValue;
  }
}

/**
 * A set of valid survey values
 */
export default class ValueSet {
  /**
   * Generate an id from a value set name.  Will log an error and return null if input name is invalid for id generation
   */
  static generateIdFromName(name: string) {
    if (isNil(name) || !isString(name)) {
      console.error('Tried to generate a id from a null or non string input');
      return null;
    }

    // make lower case and remove spaces
    const formatted = name.toLowerCase().replace(/\s/g, '');

    if (isNil(formatted) || formatted === '') {
      console.error('Input name could not be formatted into an id');
      return null;
    }

    return formatted;
  }

  /**
   * Checks if the input name is a valid value set name
   */
  static isValidValueSetName(name: string) {
    return ValueSet.generateIdFromName(name) !== null;
  }

  static initFromFhirResource(
    fhirValueSet: fhir.ValueSet = {} as fhir.ValueSet,
  ) {
    const { id, name, compose, purpose, ...rest } = fhirValueSet;

    const system = get(fhirValueSet, 'compose.include[0].system');
    const concepts = get(fhirValueSet, 'compose.include[0].concept', [])
      .filter(Boolean)
      .map((fhirConcept: fhir.ValueSetComposeIncludeConcept) => {
        const extensions =
          ExtensionCollection.initFromBackboneElement(fhirConcept);

        const numericValueUrl =
          purpose === 'survey-score-labels'
            ? surveyUrls.surveyScoreLabelNumericValue
            : surveyUrls.surveyListOptionNumericValue;
        const numericValue = extensions.getValueDecimal(numericValueUrl);

        const description =
          purpose === 'survey-score-labels'
            ? extensions.getValueMarkdown(
                surveyUrls.surveyScoreLabelMarkdownValue,
              )
            : undefined;

        return new Coding(
          fhirConcept.code,
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          fhirConcept.display,
          system,
          numericValue,
          description,
        );
      });

    return new ValueSet(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      id,
      name,
      /** The older survey lists are stored as valueSet, and do not have the purpose set.
      This is to keep backward compatibility */
      (purpose || 'survey-list') as SurveyValueSetPurpose,
      concepts,
      rest,
    );
  }

  _id: string;
  _name: string;
  _purpose: SurveyValueSetPurpose;
  _concepts: Coding[];
  _additionalData: Partial<fhir.ValueSet>;

  constructor(
    id: string,
    name: string,
    purpose: SurveyValueSetPurpose,
    concepts: Coding[] = [],
    additionalData?: Partial<fhir.ValueSet>,
  ) {
    this._id = id;
    this._name = name;
    this._purpose = purpose;
    this._concepts = concepts;
    this._additionalData = additionalData || {};
  }

  equals(valueSet: ValueSet): boolean {
    return isEqual(this.fhirResource, valueSet.fhirResource);
  }

  updateName(name: string): ValueSet {
    const newId = ValueSet.generateIdFromName(name);
    return new ValueSet(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      newId,
      name,
      this._purpose,
      this._concepts,
      this._additionalData,
    );
  }

  get name(): string {
    return this._name;
  }

  get id(): string {
    return this._id;
  }

  get concepts(): Coding[] {
    return this._concepts;
  }

  get purpose(): SurveyValueSetPurpose {
    return this._purpose;
  }

  get system(): string {
    const firstCoding = this._concepts[0];

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return firstCoding ? firstCoding.system : null;
  }

  get hasScoring(): boolean {
    return !!(this.concepts || []).find(_ => _.numericValue !== null);
  }

  getScoringValue(coding: fhir.Coding): number | null {
    const rawNumericValue = this.concepts.find(
      _ => _.code === coding?.code,
    )?.numericValue;
    return isNil(rawNumericValue) ? null : rawNumericValue;
  }

  get fhirResource(): fhir.ValueSet {
    const status = get<Partial<fhir.ValueSet>, 'status', string>(
      this._additionalData,
      'status',
      'active',
    );

    return {
      resourceType: 'ValueSet',
      id: this.id,
      name: this.name,
      purpose: this.purpose,
      compose: {
        include: [
          {
            system: this.system,
            concept: this._concepts.map(
              concept => concept.fhirCoding,
            ) as fhir.ValueSetComposeIncludeConcept[],
          },
        ],
      },
      ...this._additionalData,
      status,
    };
  }
}
